import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/core/Materials/standardMaterial';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import remove from 'lodash/remove';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';

import { PBRCustomMaterial } from '@babylonjs/materials/custom/pbrCustomMaterial';
import { PatternMesh } from './Pattern/Mesh/patternMesh';
import { ITextGeometry, IVariantText } from './Pattern/Decal/patternText';
import { IMaskGeometry, IVariantMask } from './Pattern/Decal/patternMask';
import { IContentOptions } from './content';
import { SystemLayer } from '../System/systemLayer';
import { IArcCameraAngles } from '../System/systemCamera';
import { ContentModelAnimation } from './contentModelAnimation';
import { Animation } from '../System/Animation/animation';
import { Ease, EaseMode } from '../System/Animation/animationEasing';
import { clonePBRCustom } from './Pattern/Mesh/patternMaterial';

export interface IVariantMesh {
    mesh: string,
    material: string,
}

export interface IVariantMeshes {
    meshes: string[],
    material: string,
}

export interface IVariantMeshesMaterials {
    meshes: string[],
    materials?: string[],
    decalGeometry?: ITextGeometry | IMaskGeometry,
}

export interface IVariantMeshesProperties extends IVariantMeshesMaterials {
    nakerName?: string,
    cameraView?: IArcCameraAngles,
}

export abstract class ContentModelVariantMesh extends ContentModelAnimation {
    public variantMeshesPropertiesList: IVariantMeshesProperties[] = []

    protected variantMeshesList: IVariantMeshes[] = []

    protected variantMeshList: IVariantMesh[] = []

    protected variantAnimation: Animation

    constructor(systemLayer: SystemLayer, content: IContentOptions) {
        super(systemLayer, content);
        this.variantAnimation = new Animation(this._system);
        this.variantAnimation.setEasing(Ease.Circle, EaseMode.Out);
    }

    public getVariantMeshesByNakerName(nakerName: string): IVariantMeshes {
        const variantMeshesProperties = this.getVariantMeshesPropertiesByNakerName(nakerName);
        if (variantMeshesProperties) {
            const variantMeshes = find(
                this.variantMeshesList,
                (v) => isEqual(v.meshes, variantMeshesProperties.meshes)
            );
            return variantMeshes;
        }
        return null;
    }

    public getVariantMeshesByMeshes(meshes: string[]): IVariantMeshes {
        return find(this.variantMeshesList, (v) => isEqual(v.meshes, meshes));
    }

    public getVariantMeshesPropertiesByMeshes(meshes: string[]): IVariantMeshesProperties {
        return find(this.variantMeshesPropertiesList, (v) => isEqual(v.meshes, meshes));
    }

    public getVariantMeshesPropertiesByNakerName(nakerName: string): IVariantMeshesProperties {
        return find(
            this.variantMeshesPropertiesList,
            (v) => v.nakerName === nakerName || v.meshes[0] === nakerName
        );
    }

    public getVariantMeshesMaterialsList(): IVariantMeshesProperties[] {
        return filter(
            cloneDeep(this.variantMeshesPropertiesList),
            (vmm) => vmm.materials !== undefined && vmm.materials.length > 0
        );
    }

    public doesVariantMeshesExist(variantMeshes: IVariantMeshes): boolean {
        const vmProps = this.getVariantMeshesPropertiesByMeshes(variantMeshes.meshes);
        if (vmProps) {
            return find(
                vmProps.materials,
                (m) => isEqual(m, variantMeshes.material)
            ) !== undefined;
        }
        return false;
    }

    public setMeshesMaterials(meshes: string[], materials: string[]): void {
        const variantMeshesMaterials = this.getVariantMeshesPropertiesByMeshes(meshes);
        if (variantMeshesMaterials) variantMeshesMaterials.materials = materials;
    }

    public getSelectedVariantMeshes(
        meshes: string[]
    ): IVariantMeshes | IVariantText | IVariantMask {
        return this.getVariantMeshesByMeshes(meshes);
    }

    public getVariantMeshesList(): IVariantMeshes[] {
        return cloneDeep(this.variantMeshesList);
    }

    protected setAllVariantMeshesMaterial(
        variantMeshesList: IVariantMeshes[],
        animate?: boolean
    ): void {
        this.removeAnimMesh();
        variantMeshesList.forEach((variantMeshes) => {
            this._setVariantMeshesMaterial(variantMeshes, animate);
        });
    }

    private _setVariantMeshesMaterial(variantMeshes: IVariantMeshes, animate?: boolean) {
        this.saveVariantMeshes(variantMeshes);
        variantMeshes.meshes.forEach((mesh) => {
            this._setVariantMesh({ mesh, material: variantMeshes.material }, animate);
        });
    }

    public setVariantMeshesMaterial(variantMeshes: IVariantMeshes, animate?: boolean): void {
        this.removeAnimMesh();
        this.saveVariantMeshes(variantMeshes);
        variantMeshes.meshes.forEach((mesh) => {
            this.setVariantMesh({ mesh, material: variantMeshes.material }, animate);
        });
    }

    private saveVariantMeshes(variantMeshes: IVariantMeshes) {
        remove(this.variantMeshesList, (x) => isEqual(x.meshes, variantMeshes.meshes));
        this.variantMeshesList.push(variantMeshes);
    }

    protected _unsetVariantMeshes(meshes: string[]): void {
        this._setVariantMeshes({ meshes, material: null }, false);
        remove(this.variantMeshesList, (x) => isEqual(x.meshes, meshes));
    }

    //! we want to avoid calling _setVariantMesh with animation
    //! as we can have cloned mesh which stays.
    //! But we need it when we change model variant in function checkVariantChange
    protected _unsafeSetVariantMeshes(variantMeshes: IVariantMeshes, animate: boolean): void {
        this._setVariantMeshes(variantMeshes, animate);
    }

    private _setVariantMeshes(variantMeshes: IVariantMeshes, animate: boolean) {
        variantMeshes.meshes.forEach((mesh) => {
            this._setVariantMesh({ mesh, material: variantMeshes.material }, animate);
        });
    }

    public flashMeshes(meshes: string[]): void {
        meshes.forEach((mesh) => {
            const patternMesh = this.getPatternMeshByName(mesh);
            if (patternMesh) patternMesh.flash();
        });
    }

    /* ------------------ VARIANTMESH ------------------ */

    public getVariantMeshList(): IVariantMesh[] {
        return cloneDeep(this.variantMeshList);
    }

    public getVariantMeshByName(name: string): IVariantMesh {
        return find(this.variantMeshList, (c) => c.mesh === name);
    }

    public _unsetVariantMesh(meshName: string): void {
        this._setVariantMesh({ mesh: meshName, material: null }, false);
        remove(this.variantMeshList, (x) => x.mesh === meshName);
    }

    public setVariantMesh(variant: IVariantMesh, animate: boolean): void {
        // Stop before change so that animation doesn't delete new cloned meshes
        this.variantAnimation.stop();
        this._setVariantMesh(variant, animate, () => {
            this.animateVariant();
        });
    }

    private _setVariantMesh(variant: IVariantMesh, animate: boolean, callback?: () => void) {
        if (!variant) return;

        const { patternMeshes } = this.patternModel;
        const patternMesh: PatternMesh = find(
            patternMeshes,
            (m) => variant.mesh === m.mesh.name
        );
        remove(this.variantMeshList, (x) => x.mesh === variant.mesh);

        const tM = this._system.toolMaterial;
        const material = tM.getMaterialWithLinearSpaceByKey(variant.material);
        if (!material || !patternMesh) return;
        this.variantMeshList.push(variant);

        if (animate) {
            // Cloning takes time. So for the animation to work,
            // we need to wait before changing the material
            this.checkClonedPattern(patternMesh, () => {
                patternMesh.applyMaterial(material);
                if (callback) callback();
            });
        } else {
            patternMesh.applyMaterial(material);
        }
    }

    private checkClonedPattern(patternMesh: PatternMesh, callback: () => void): boolean {
        let cloned = false;
        const cloneKey = `${patternMesh.key}_clone`;
        let clonedMesh = find(this.clonedMeshes, (p) => p.id === cloneKey);
        if (!clonedMesh) {
            cloned = true;
            clonedMesh = patternMesh.mesh.clone(null);
            clonedMesh.id = cloneKey;
            // Cloning takes a lot of performance
            // So we store the cloned Mesh so that we don't need to clone again later
            this.clonedMeshes.push(clonedMesh);
        } else {
            clonedMesh.visibility = 1;
        }

        const material = clonePBRCustom(patternMesh.material as PBRCustomMaterial);
        // material.onBindObservable.addOnce(() => {
        setTimeout(() => {
            this.variantAnimation.stop();
            callback();
        }, 100);
        // });
        // Need to have rendering or bind event not triggered
        this.variantAnimation.infinite(() => {});
        this._system.checkActiveMeshes();

        clonedMesh.material = material;

        this.animMeshes.push(clonedMesh);
        return cloned;
    }

    private clonedMeshes: Mesh[] = []

    private animMeshes: Mesh[] = []

    protected animateVariant(): void {
        // this._system.checkActiveMeshes();
        this.variantAnimation.simple(100, (perc) => {
            this.setAnimMaterialAlpha(1 - perc);
        }, () => {
            this.setAnimMaterialAlpha(0);
            this.removeAnimMesh();
        });
    }

    private setAnimMaterialAlpha(alpha: number) {
        this.animMeshes.forEach((animMesh) => {
            animMesh.visibility = alpha;
            // const { material } = animMesh;
            // material.onBindObservable.addOnce(() => {
            //     material.getEffect().setFloat('alphaTransition', alpha);
            // });
        });
    }

    protected removeAnimMesh(): void {
        this.animMeshes.forEach((animMesh) => {
            animMesh.visibility = 0;
            if (animMesh.material) animMesh.material.dispose();
        });
        this._system.checkActiveMeshes();
        this.animMeshes = [];
    }

    /* ------------------ OTHER ------------------ */

    protected getPatternMeshByName(name: string): PatternMesh {
        return find(this.patternModel.patternMeshes, (m) => name === m.mesh.name);
    }
}
